<template>
  <div class="ptc-dashboard">

    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-badge">
          <feather-icon icon="BarChart2Icon" size="14" />
          <span>PTC</span>
        </div>
        <h2 class="page-title">Dashboard Operacional</h2>
        <p class="page-subtitle">Acompanhe em tempo real pedidos, notas fiscais e títulos</p>
      </div>
      <div class="header-right">
        <button class="btn-refresh-header" @click="refreshDashboard" title="Atualizar dados">
          <feather-icon icon="RefreshCcwIcon" size="16" />
          <span>Atualizar</span>
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <b-overlay :show="$store.getters['PTC_Dashboard/getIsLoading']" class="stats-overlay" rounded="lg">
      <div class="stats-grid">

        <div class="stat-card stat-primary">
          <div class="stat-icon-wrap">
            <div class="stat-icon">
              <feather-icon icon="ShoppingBagIcon" size="22" />
            </div>
            <div class="stat-ring"></div>
          </div>
          <div class="stat-body">
            <span class="stat-label">Total de Pedidos</span>
            <span class="stat-value">{{ $store.state.PTC_Dashboard.RetornaQTDPedidosDashPTC.qtdTotalPedido || 0 }}</span>
            <span class="stat-hint">no período selecionado</span>
          </div>
          <div class="stat-bg-shape"></div>
        </div>

        <div class="stat-card stat-success">
          <div class="stat-icon-wrap">
            <div class="stat-icon">
              <feather-icon icon="FileTextIcon" size="22" />
            </div>
            <div class="stat-ring"></div>
          </div>
          <div class="stat-body">
            <span class="stat-label">Notas Fiscais</span>
            <span class="stat-value">{{ $store.state.PTC_Dashboard.RetornaQTDNotasDashPTC.qtdTotalNotas || 0 }}</span>
            <span class="stat-hint">emitidas no período</span>
          </div>
          <div class="stat-bg-shape"></div>
        </div>

        <div class="stat-card stat-warning">
          <div class="stat-icon-wrap">
            <div class="stat-icon">
              <feather-icon icon="CreditCardIcon" size="22" />
            </div>
            <div class="stat-ring"></div>
          </div>
          <div class="stat-body">
            <span class="stat-label">Títulos</span>
            <span class="stat-value">{{ $store.state.PTC_Dashboard.qtdTitulosDashboardPTC.qtdTotalTitulos || 0 }}</span>
            <span class="stat-hint">em carteira</span>
          </div>
          <div class="stat-bg-shape"></div>
        </div>

        <div class="stat-card stat-danger">
          <div class="stat-icon-wrap">
            <div class="stat-icon">
              <feather-icon icon="AlertCircleIcon" size="22" />
            </div>
            <div class="stat-ring"></div>
          </div>
          <div class="stat-body">
            <span class="stat-label">Títulos Vencidos</span>
            <span class="stat-value">{{ $store.state.PTC_Dashboard.qtdTitulosDashboardPTC.qtdTotalTitulosVencidos || 0 }}</span>
            <span class="stat-hint">requerem atenção</span>
          </div>
          <div class="stat-bg-shape"></div>
        </div>

      </div>
    </b-overlay>

    <!-- Filters Section -->
    <div class="filters-panel">
      <div class="filters-panel-header">
        <div class="filters-title">
          <feather-icon icon="SlidersIcon" size="16" />
          <span>Filtrar Período</span>
        </div>
        <div class="filters-actions-inline">
          <button class="btn-icon-only" @click="refreshDashboard" title="Redefinir para 1 ano">
            <feather-icon icon="RotateCcwIcon" size="15" />
          </button>
        </div>
      </div>
      <div class="filters-panel-body">
        <div class="filter-field">
          <label class="filter-label">
            <feather-icon icon="CalendarIcon" size="13" />
            Data Inicial
          </label>
          <b-form-input
            id="datepicker-start"
            v-model="tempStartDate"
            type="date"
            class="filter-input"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label">
            <feather-icon icon="CalendarIcon" size="13" />
            Data Final
          </label>
          <b-form-input
            id="datepicker-end"
            v-model="tempEndDate"
            type="date"
            class="filter-input"
          />
        </div>
        <div class="filter-submit">
          <button class="btn-apply-filter" @click="filterDashboard">
            <feather-icon icon="SearchIcon" size="15" />
            <span>Aplicar Filtro</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Section: Pedidos -->
    <div class="section-block">
      <div class="section-header">
        <div class="section-title-group">
          <div class="section-icon-badge primary">
            <feather-icon icon="ShoppingBagIcon" size="16" />
          </div>
          <h4 class="section-title">Pedidos</h4>
        </div>
      </div>

      <b-overlay
        :show="$store.state.PTC_Dashboard.RetornaQTDPedidosDashPTC && $store.state.PTC_Dashboard.RetornaQTDPedidosDashPTC.qtdPedidos && $store.state.PTC_Dashboard.RetornaQTDPedidosDashPTC.qtdPedidos.length === 0 || $store.state.PTC_Dashboard.setIsLoading"
        rounded="lg"
      >
        <Pedido ref="dadosFiltrados" />
        <template #overlay>
          <div class="empty-overlay">
            <div v-if="$store.state.PTC_Dashboard.setIsLoading" class="loading-state">
              <b-spinner class="loading-spinner" />
              <p class="loading-text">Carregando dados...</p>
            </div>
            <div v-else class="empty-state">
              <div class="empty-icon-wrap">
                <feather-icon icon="InboxIcon" size="36" />
              </div>
              <p class="empty-title">Nenhum pedido encontrado</p>
              <p class="empty-msg">Não há pedidos para exibir no período selecionado.</p>
            </div>
          </div>
        </template>
      </b-overlay>
    </div>

    <!-- Section: Notas Fiscais -->
    <div class="section-block">
      <div class="section-header">
        <div class="section-title-group">
          <div class="section-icon-badge success">
            <feather-icon icon="FileTextIcon" size="16" />
          </div>
          <h4 class="section-title">Notas Fiscais</h4>
        </div>
      </div>

      <b-overlay
        :show="$store.state.PTC_Dashboard.RetornaQTDNotasDashPTC && $store.state.PTC_Dashboard.RetornaQTDNotasDashPTC.qtdNotas && $store.state.PTC_Dashboard.RetornaQTDNotasDashPTC.qtdNotas.length === 0 || $store.state.PTC_Dashboard.setIsLoading"
        rounded="lg"
      >
        <b-row>
          <NotaFiscal ref="atualizaParams" />
          <ChartjsHorizontalBarChart ref="dadosFiltradosTop10" />
        </b-row>
        <template #overlay>
          <div class="empty-overlay">
            <div v-if="$store.state.PTC_Dashboard.setIsLoading" class="loading-state">
              <b-spinner class="loading-spinner" />
              <p class="loading-text">Carregando dados...</p>
            </div>
            <div v-else class="empty-state">
              <div class="empty-icon-wrap">
                <feather-icon icon="FileIcon" size="36" />
              </div>
              <p class="empty-title">Nenhuma nota fiscal encontrada</p>
              <p class="empty-msg">Não há notas fiscais para exibir no período selecionado.</p>
            </div>
          </div>
        </template>
      </b-overlay>
    </div>

    <!-- Section: Títulos -->
    <div class="section-block">
      <div class="section-header">
        <div class="section-title-group">
          <div class="section-icon-badge warning">
            <feather-icon icon="CreditCardIcon" size="16" />
          </div>
          <h4 class="section-title">Títulos</h4>
        </div>
      </div>

      <b-overlay
        :show="$store.state.PTC_Dashboard.qtdTitulosDashboardPTC && $store.state.PTC_Dashboard.qtdTitulosDashboardPTC.titulos && $store.state.PTC_Dashboard.qtdTitulosDashboardPTC.titulos.length === 0 || $store.state.PTC_Dashboard.setIsLoading"
        rounded="lg"
      >
        <Titulos ref="dadosFiltradosTitulo" />
        <template #overlay>
          <div class="empty-overlay">
            <div v-if="$store.state.PTC_Dashboard.setIsLoading" class="loading-state">
              <b-spinner class="loading-spinner" />
              <p class="loading-text">Carregando dados...</p>
            </div>
            <div v-else class="empty-state">
              <div class="empty-icon-wrap">
                <feather-icon icon="CreditCardIcon" size="36" />
              </div>
              <p class="empty-title">Nenhum título encontrado</p>
              <p class="empty-msg">Não há títulos para exibir no período selecionado.</p>
            </div>
          </div>
        </template>
      </b-overlay>
    </div>

  </div>
</template>

<script>
import {
  BRow, BFormInput, BOverlay, BSpinner,
} from 'bootstrap-vue';
import moment from 'moment';
import PTC_DashboardService from '@/services/PTC/PTC_Dashboard.service';
import Pedido from '@/views/app/PTC/DashBoard/Pedido/pedido.vue';
import NotaFiscal from '@/views/app/PTC/DashBoard/NotasFiscais/nfc.vue';
import Titulos from '@/views/app/PTC/DashBoard/Titulos/titulos.vue';
import ChartjsHorizontalBarChart from '@/views/app/PTC/DashBoard/analytics/chart/ChartjsHorizontalBarChart.vue';

const ptc_dashboardService = new PTC_DashboardService();

export default {
  components: {
    BRow,
    BFormInput,
    BOverlay,
    BSpinner,
    Pedido,
    NotaFiscal,
    Titulos,
    ChartjsHorizontalBarChart,
  },
  data() {
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    return {
      tempStartDate: oneYearAgo.toISOString().substr(0, 10),
      tempEndDate: today.toISOString().substr(0, 10),
      startDate: null,
      endDate: null,
    };
  },
  methods: {
    buildQuery(startDate, endDate) {
      const query = { dataInicial: startDate, dataFinal: endDate };
      const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
      // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
      if (perfilLogado && ['Cliente', 'ClienteFinal'].includes(perfilLogado.perfil_acesso_nome)) {
        const clienteData = JSON.parse(localStorage.getItem('clienteData'));
        if (clienteData && clienteData.cliente && clienteData.cliente.clienteid) {
          query.clienteId = clienteData.cliente.clienteid;
        }
      }
      return query;
    },

    async refreshDashboard() {
      const today = moment().format('YYYY-MM-DD');
      const oneYearAgo = moment().subtract(1, 'year').format('YYYY-MM-DD');
      this.tempStartDate = oneYearAgo;
      this.tempEndDate = today;
      const query = this.buildQuery(oneYearAgo, today);
      await ptc_dashboardService.RetornaCardsTotais(query);
      this.$refs.dadosFiltrados.atualizarDados(query);
      this.$refs.atualizaParams.atualizaParams(query);
      this.$refs.dadosFiltradosTop10.dadosFiltrados10();
      this.$refs.dadosFiltradosTitulo.atualizarGraficoTitulo();
    },

    async filterDashboard() {
      const { tempStartDate, tempEndDate } = this;
      const formattedStart = moment(tempStartDate, 'YYYY-MM-DD').format('YYYY-MM-DD');
      const formattedEnd = moment(tempEndDate, 'YYYY-MM-DD').format('YYYY-MM-DD');
      this.startDate = formattedStart;
      this.endDate = formattedEnd;
      const query = this.buildQuery(formattedStart, formattedEnd);
      await ptc_dashboardService.RetornaCardsTotais(query);
      this.$refs.dadosFiltrados.atualizarDados(query);
      this.$refs.atualizaParams.atualizaParams(query);
      this.$refs.dadosFiltradosTop10.dadosFiltrados10();
      this.$refs.dadosFiltradosTitulo.atualizarGraficoTitulo();
    },
  },
  mounted() {
    this.refreshDashboard();
  },
};
</script>

<style scoped lang="scss">
/*
  B2B Premium Palette (PTC Dashboard):
  1: #01094e  – Deep Navy
  2: var(--primary, #0575b5)  – Ocean Blue
  3: var(--secondary, #00b7d6)  – Vibrant Cyan
  4: #8fdeea  – Pale Sky Blue
  5: #d6f4fa  – Ice Blue
*/

/* ─── Base ─────────────────────────────────────────────── */
.ptc-dashboard {
  padding: 0 0 2rem;
}

/* ─── Page Header ───────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
}

.header-left { display: flex; flex-direction: column; gap: 0.25rem; }

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: linear-gradient(135deg, rgba(var(--primary-rgb, 5, 117, 181), 0.12), rgba(var(--secondary-rgb, 0, 183, 214), 0.12));
  border: 1px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.3);
  color: var(--primary, #0575b5);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  width: fit-content;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #01094e;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.page-subtitle {
  color: var(--primary, #0575b5);
  font-size: 0.95rem;
  font-weight: 400;
  margin: 0;
  opacity: 0.8;
}

.btn-refresh-header {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.25rem;
  border-radius: 10px;
  border: 1px solid rgba(143, 222, 234, 0.5);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  color: var(--primary, #0575b5);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(var(--secondary-rgb, 0, 183, 214), 0.08);
    border-color: var(--secondary, #00b7d6);
    color: var(--secondary, #00b7d6);
    transform: translateY(-1px);
  }
}

/* ─── Stats Grid ────────────────────────────────────────── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.75rem;
}

.stat-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 24px rgba(1, 9, 78, 0.06);
  border: 1px solid rgba(143, 222, 234, 0.35);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
              box-shadow 0.3s ease,
              border-color 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 48px rgba(1, 9, 78, 0.1);
    border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.45);
  }
}

.stat-icon-wrap {
  position: relative;
  flex-shrink: 0;
}

.stat-icon {
  width: 58px;
  height: 58px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(var(--primary-rgb, 5, 117, 181), 0.10), rgba(var(--secondary-rgb, 0, 183, 214), 0.10));
  color: var(--primary, #0575b5);
  border: 1px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.18);
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.stat-ring {
  position: absolute;
  inset: -4px;
  border-radius: 18px;
  border: 2px solid rgba(var(--secondary-rgb, 0, 183, 214), 0);
  transition: all 0.3s ease;
}

.stat-card:hover .stat-icon {
  background: linear-gradient(135deg, var(--primary, #0575b5), var(--secondary, #00b7d6));
  color: #fff;
  box-shadow: 0 6px 18px rgba(var(--secondary-rgb, 0, 183, 214), 0.45);
  border-color: transparent;
}

.stat-card:hover .stat-ring {
  border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.25);
}

// Variant accent left borders
.stat-card.stat-primary { border-left: 4px solid var(--primary, #0575b5); }
.stat-card.stat-success { border-left: 4px solid var(--secondary, #00b7d6); }
.stat-card.stat-warning { border-left: 4px solid #f59e0b; }
.stat-card.stat-danger  { border-left: 4px solid #ef4444; }

.stat-card.stat-warning .stat-icon { color: #f59e0b; }
.stat-card.stat-danger  .stat-icon { color: #ef4444; }
.stat-card.stat-warning:hover .stat-icon { background: linear-gradient(135deg, #f59e0b, #fbbf24); box-shadow: 0 6px 18px rgba(245, 158, 11, 0.4); }
.stat-card.stat-danger:hover  .stat-icon { background: linear-gradient(135deg, #ef4444, #f87171); box-shadow: 0 6px 18px rgba(239, 68, 68, 0.4); }

.stat-body {
  display: flex;
  flex-direction: column;
  z-index: 1;
  min-width: 0;
}

.stat-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--primary, #0575b5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
  margin-bottom: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #01094e;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.stat-hint {
  font-size: 0.75rem;
  color: var(--primary, #0575b5);
  opacity: 0.55;
  margin-top: 0.2rem;
}

.stat-bg-shape {
  position: absolute;
  right: -18px;
  bottom: -18px;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(var(--secondary-rgb, 0, 183, 214), 0.08), transparent 70%);
}

/* ─── Filters Panel ─────────────────────────────────────── */
.filters-panel {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(143, 222, 234, 0.3);
  box-shadow: 0 4px 24px rgba(1, 9, 78, 0.05);
  margin-bottom: 2rem;
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 32px rgba(1, 9, 78, 0.08);
  }
}

.filters-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: linear-gradient(90deg, rgba(var(--primary-rgb, 5, 117, 181), 0.05), rgba(var(--secondary-rgb, 0, 183, 214), 0.03));
  border-bottom: 1px solid rgba(143, 222, 234, 0.25);
}

.filters-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.95rem;
  color: #01094e;

  &::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 16px;
    border-radius: 2px;
    background: linear-gradient(180deg, var(--primary, #0575b5), var(--secondary, #00b7d6));
    margin-right: 0.2rem;
  }
}

.btn-icon-only {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(143, 222, 234, 0.5);
  background: transparent;
  color: var(--primary, #0575b5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--secondary-rgb, 0, 183, 214), 0.08);
    border-color: var(--secondary, #00b7d6);
    color: var(--secondary, #00b7d6);
  }
}

.filters-panel-body {
  display: flex;
  align-items: flex-end;
  gap: 1.25rem;
  padding: 1.25rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
}

.filter-field {
  flex: 1;
  min-width: 160px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--primary, #0575b5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.45rem;
}

.filter-input {
  border: 1px solid rgba(143, 222, 234, 0.55) !important;
  border-radius: 10px !important;
  color: #01094e !important;
  font-size: 0.9rem !important;
  height: 40px !important;
  transition: all 0.2s ease !important;

  &:focus {
    border-color: var(--secondary, #00b7d6) !important;
    box-shadow: 0 0 0 3px rgba(var(--secondary-rgb, 0, 183, 214), 0.15) !important;
    outline: none !important;
  }
}

.filter-submit {
  @media (max-width: 640px) { width: 100%; }
}

.btn-apply-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.5rem;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, var(--primary, #0575b5), var(--secondary, #00b7d6));
  color: #fff;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 14px rgba(var(--secondary-rgb, 0, 183, 214), 0.35);
  white-space: nowrap;

  @media (max-width: 640px) { width: 100%; justify-content: center; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(var(--secondary-rgb, 0, 183, 214), 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 14px rgba(var(--secondary-rgb, 0, 183, 214), 0.35);
  }
}

/* ─── Section Blocks ────────────────────────────────────── */
.section-block {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.section-title-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-icon-badge {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.primary {
    background: linear-gradient(135deg, rgba(var(--primary-rgb, 5, 117, 181), 0.12), rgba(var(--secondary-rgb, 0, 183, 214), 0.12));
    color: var(--primary, #0575b5);
    border: 1px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
  }
  &.success {
    background: linear-gradient(135deg, rgba(var(--secondary-rgb, 0, 183, 214), 0.12), rgba(22, 163, 74, 0.08));
    color: var(--secondary, #00b7d6);
    border: 1px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
  }
  &.warning {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(251, 191, 36, 0.08));
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.2);
  }
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #01094e;
  margin: 0;
  letter-spacing: -0.01em;
}

/* ─── Empty / Loading States ────────────────────────────── */
.empty-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-spinner {
  color: var(--primary, #0575b5) !important;
  width: 2.5rem !important;
  height: 2.5rem !important;
}

.loading-text {
  font-size: 0.9rem;
  color: var(--primary, #0575b5);
  font-weight: 500;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  max-width: 320px;
}

.empty-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(var(--primary-rgb, 5, 117, 181), 0.08), rgba(var(--secondary-rgb, 0, 183, 214), 0.08));
  border: 2px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #0575b5);
  opacity: 0.7;
  margin-bottom: 0.5rem;
}

.empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: #01094e;
  margin: 0;
}

.empty-msg {
  font-size: 0.85rem;
  color: var(--primary, #0575b5);
  opacity: 0.7;
  margin: 0;
  line-height: 1.5;
}

/* ─── Deep overrides for child card components ───────────── */
::v-deep .card {
  border-radius: 14px !important;
  box-shadow: 0 4px 24px rgba(1, 9, 78, 0.05) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(8px) !important;
  border: 1px solid rgba(143, 222, 234, 0.28) !important;
  transition: box-shadow 0.3s ease !important;

  &:hover {
    box-shadow: 0 8px 32px rgba(1, 9, 78, 0.08) !important;
  }

  .card-header {
    padding: 1.25rem 1.5rem 0.75rem !important;
    background: transparent !important;
    border-bottom: 1px solid rgba(143, 222, 234, 0.2) !important;

    .card-title {
      color: #01094e !important;
      font-weight: 700 !important;
      font-size: 1.05rem !important;
      letter-spacing: -0.01em !important;
      margin: 0 !important;
    }
  }

  .card-body {
    padding: 1rem 1.5rem 1.5rem !important;
    color: #5e5873 !important;
  }
}

::v-deep .table {
  border-radius: 10px;
  overflow: hidden;

  thead th {
    background: rgba(var(--primary-rgb, 5, 117, 181), 0.06) !important;
    color: var(--primary, #0575b5) !important;
    font-weight: 700 !important;
    font-size: 0.8rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
    border-bottom: 2px solid rgba(var(--secondary-rgb, 0, 183, 214), 0.2) !important;
    padding: 0.875rem 1rem !important;
  }

  tbody td {
    vertical-align: middle !important;
    font-size: 0.9rem !important;
    padding: 0.75rem 1rem !important;
    color: #01094e !important;
    border-color: rgba(143, 222, 234, 0.15) !important;
  }

  tbody tr {
    transition: background 0.15s ease;
    &:hover td {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.04) !important;
    }
  }
}

::v-deep .btn-primary {
  background: linear-gradient(135deg, var(--primary, #0575b5), var(--secondary, #00b7d6)) !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  padding: 0.35rem 0.85rem !important;
  box-shadow: 0 3px 10px rgba(var(--secondary-rgb, 0, 183, 214), 0.3) !important;
  transition: all 0.2s ease !important;

  &:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 16px rgba(var(--secondary-rgb, 0, 183, 214), 0.4) !important;
  }
}

/* ─── Dark Layout Overrides ─────────────────────────────── */
body.dark-layout {
  .ptc-dashboard {
    .page-title { color: #d6f4fa; }
    .page-subtitle { color: #8fdeea; }

    .header-badge {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
      border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
      color: #8fdeea;
    }

    .btn-refresh-header {
      background: rgba(1, 9, 78, 0.5);
      border-color: rgba(143, 222, 234, 0.2);
      color: #8fdeea;

      &:hover {
        background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
        border-color: var(--secondary, #00b7d6);
        color: var(--secondary, #00b7d6);
      }
    }

    .stat-card {
      background: rgba(1, 9, 78, 0.55);
      border-color: rgba(143, 222, 234, 0.12);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);

      &:hover {
        background: rgba(1, 9, 78, 0.75);
        border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.3);
        box-shadow: 0 16px 48px rgba(var(--secondary-rgb, 0, 183, 214), 0.12);
      }
    }

    .stat-label { color: #8fdeea; }
    .stat-value { color: #d6f4fa; }
    .stat-hint  { color: #8fdeea; }

    .stat-icon {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
      color: var(--secondary, #00b7d6);
      border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
    }

    .stat-card.stat-warning .stat-icon { color: #fbbf24; }
    .stat-card.stat-danger  .stat-icon { color: #f87171; }

    .stat-card:hover .stat-icon {
      background: linear-gradient(135deg, var(--primary, #0575b5), var(--secondary, #00b7d6));
      color: #fff;
      box-shadow: 0 6px 18px rgba(var(--secondary-rgb, 0, 183, 214), 0.5);
      border-color: transparent;
    }

    .stat-card.stat-warning:hover .stat-icon {
      background: linear-gradient(135deg, #f59e0b, #fbbf24);
    }
    .stat-card.stat-danger:hover .stat-icon {
      background: linear-gradient(135deg, #ef4444, #f87171);
    }

    .filters-panel {
      background: rgba(1, 9, 78, 0.5);
      border-color: rgba(143, 222, 234, 0.1);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
    }

    .filters-panel-header {
      background: rgba(1, 9, 78, 0.6);
      border-bottom-color: rgba(143, 222, 234, 0.12);
    }

    .filters-title { color: #d6f4fa; }

    .btn-icon-only {
      border-color: rgba(143, 222, 234, 0.2);
      color: #8fdeea;

      &:hover {
        background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
        border-color: var(--secondary, #00b7d6);
        color: var(--secondary, #00b7d6);
      }
    }

    .filter-label { color: #8fdeea; }

    .filter-input {
      background: rgba(143, 222, 234, 0.05) !important;
      border-color: rgba(143, 222, 234, 0.2) !important;
      color: #d6f4fa !important;

      &:focus {
        border-color: var(--secondary, #00b7d6) !important;
        background: rgba(var(--secondary-rgb, 0, 183, 214), 0.07) !important;
      }
    }

    .section-title { color: #d6f4fa; }

    .section-icon-badge.primary {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
      color: var(--secondary, #00b7d6);
      border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
    }
    .section-icon-badge.success {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.1);
      color: var(--secondary, #00b7d6);
      border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.2);
    }
    .section-icon-badge.warning {
      background: rgba(245, 158, 11, 0.1);
      color: #fbbf24;
      border-color: rgba(245, 158, 11, 0.2);
    }

    .empty-icon-wrap {
      background: rgba(var(--secondary-rgb, 0, 183, 214), 0.08);
      border-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.15);
      color: var(--secondary, #00b7d6);
    }
    .empty-title { color: #d6f4fa; }
    .empty-msg   { color: #8fdeea; }
    .loading-text { color: #8fdeea; }

    ::v-deep .card {
      background: rgba(1, 9, 78, 0.55) !important;
      border-color: rgba(143, 222, 234, 0.1) !important;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3) !important;

      .card-header {
        border-bottom-color: rgba(143, 222, 234, 0.12) !important;

        .card-title { color: #d6f4fa !important; }
      }

      .card-body { color: #8fdeea !important; }
    }

    ::v-deep .table {
      thead th {
        background: rgba(var(--secondary-rgb, 0, 183, 214), 0.06) !important;
        color: #8fdeea !important;
        border-bottom-color: rgba(var(--secondary-rgb, 0, 183, 214), 0.15) !important;
      }

      tbody td {
        color: #d6f4fa !important;
        border-color: rgba(143, 222, 234, 0.08) !important;
      }

      tbody tr:hover td {
        background: rgba(var(--secondary-rgb, 0, 183, 214), 0.06) !important;
      }
    }
  }
}
</style>
